import * as Dialog from "@radix-ui/react-dialog"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { X, Banknote, CreditCard, Download, RotateCcw, IdCard, CheckCircle2, AlertCircle } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useState } from "react"
import { api, ApiRequestError } from "@/lib/api"
import { formatCurrency, formatDate, LOCALE_MAP } from "@/lib/utils"
import { cn } from "@/lib/utils"
import type { AdminReservationRow, BeskydCardSendResponse } from "@fohet/caravan-booker-shared/types"
import { GuestUbyportEdit } from "./GuestUbyportEdit"

interface ReservationDetailModalProps {
    reservation: AdminReservationRow | null
    onClose: () => void
    queryKey: unknown[]
    currency: string
}

const statusColor: Record<string, string> = {
    created: "bg-orange-50 text-orange-700 border-orange-200",
    paid: "bg-green-50 text-green-700 border-green-200",
    confirmed: "bg-blue-50 text-blue-700 border-blue-200",
    active: "bg-brand/10 text-green-800 border-brand/30",
    completed: "bg-slate-100 text-slate-600 border-slate-200",
    cancelled: "bg-red-50 text-red-600 border-red-200",
}

export const ReservationDetailModal = ({ reservation, onClose, queryKey, currency }: ReservationDetailModalProps) => {
    const { t, i18n } = useTranslation()
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"
    const queryClient = useQueryClient()
    // Which guest's Ubyport-fields edit form is open (index), or null.
    const [editingGuest, setEditingGuest] = useState<number | null>(null)

    // Mark-paid / cancel / reactivate change payment_status or status —
    // both feed into Vyúčtování Kempi (which is keyed by ["admin", "billing"])
    // as well as the dashboard reservation list. Invalidate both so the
    // user doesn't need to F5 after toggling a payment.
    const invalidateRelevantQueries = () => {
        queryClient.invalidateQueries({ queryKey })
        queryClient.invalidateQueries({ queryKey: ["admin", "billing"] })
    }

    const markPaidMutation = useMutation({
        mutationFn: ({ id, method }: { id: string; method: string }) =>
            api.patch(`/reservations/${id}`, { paymentStatus: "paid", status: "paid", paymentMethod: method }),
        onSuccess: () => {
            toast.success(t("admin.table.toastMarkedPaid"))
            invalidateRelevantQueries()
            onClose()
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.table.toastUpdateFailed"))
        },
    })

    const cancelMutation = useMutation({
        mutationFn: (id: string) =>
            api.patch(`/reservations/${id}`, { status: "cancelled" }),
        onSuccess: () => {
            toast.success(t("admin.table.toastCancelled"))
            invalidateRelevantQueries()
            onClose()
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.table.toastCancelFailed"))
        },
    })

    const reactivateMutation = useMutation({
        mutationFn: ({ id, method }: { id: string; method: string }) =>
            api.patch(`/reservations/${id}`, { status: "paid", paymentStatus: "paid", paymentMethod: method }),
        onSuccess: () => {
            toast.success(t("admin.detail.toastReactivated"))
            invalidateRelevantQueries()
            onClose()
        },
        onError: (err) => {
            if (err instanceof ApiRequestError) {
                if (err.code === "SPOT_CONFLICT") {
                    toast.error(t("admin.detail.toastReactivateSpotTaken"))
                    return
                }
                if (err.code === "NO_SPOT_ASSIGNED") {
                    toast.error(t("admin.detail.toastReactivateNoSpot"))
                    return
                }
                toast.error(err.message)
                return
            }
            toast.error(t("admin.detail.toastReactivateFailed"))
        },
    })

    // BVC config check — only show the "Send to BVC" button if the camp
    // has a token saved. We deliberately don't gate on `enabled` here:
    // the toggle is for auto-submission, manual send works regardless.
    const { data: bvcStatus } = useQuery({
        queryKey: ["bvc-config", reservation?.arealId],
        queryFn: () => api.get<{ hasToken: boolean }>(`/areals/${reservation!.arealId}/beskyd-card-config`),
        enabled: !!reservation?.arealId,
    })

    const sendBvcMutation = useMutation({
        mutationFn: (id: string) =>
            api.post<BeskydCardSendResponse>(`/reservations/${id}/beskyd-card/send`, {}),
        onSuccess: (data) => {
            const okCount = data.results.filter(r => r.status === "preregistered").length
            const errCount = data.results.length - okCount
            if (errCount === 0) {
                toast.success(t("admin.detail.bvc.allOk", { count: okCount }))
            } else if (okCount === 0) {
                toast.error(t("admin.detail.bvc.allFailed", { count: errCount }))
            } else {
                toast.warning(t("admin.detail.bvc.partial", { ok: okCount, err: errCount }))
            }
            queryClient.invalidateQueries({ queryKey })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.detail.bvc.failed"))
        },
    })

    const r = reservation
    if (!r) return null

    const bvcSent = r.guests.some(g => g.bvcStatus === "preregistered")
    const bvcAvailable = bvcStatus?.hasToken === true && r.status !== "cancelled"

    const primaryGuest = r.guests[0]
    const isPending = r.paymentStatus === "pending" && r.status !== "cancelled"

    return (
        <Dialog.Root open={!!reservation} onOpenChange={(open) => { if (!open) onClose() }}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
                <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-[40px] shadow-2xl p-8 outline-none">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <span className="text-4xl font-black text-slate-900">
                                    {r.spotNumber ? `#${r.spotNumber}` : "—"}
                                </span>
                                <span className="text-2xl font-black text-slate-300">·</span>
                                <span className="text-2xl font-black text-slate-700">{r.licensePlate}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={cn(
                                    "inline-block px-2 py-0.5 rounded-lg border text-xs font-black capitalize",
                                    statusColor[r.status] ?? "bg-slate-100 text-slate-600 border-slate-200",
                                )}>
                                    {t(`common.status.${r.status}`, { defaultValue: r.status })}
                                </span>
                                <span className="inline-block px-2 py-0.5 rounded-lg border text-xs font-black bg-slate-50 text-slate-600 border-slate-200">
                                    {t(`admin.detail.paymentMethod.${r.paymentMethod}`, { defaultValue: r.paymentMethod })}
                                </span>
                                {r.hasPet && (
                                    <span className="inline-block px-2 py-0.5 rounded-lg border text-xs font-black bg-amber-50 text-amber-700 border-amber-200">
                                        {t("admin.detail.hasPet")}
                                    </span>
                                )}
                            </div>
                        </div>
                        <Dialog.Close className="rounded-2xl p-2 hover:bg-slate-100 transition-colors shrink-0 ml-4">
                            <X className="size-5 text-slate-400" />
                        </Dialog.Close>
                    </div>

                    {/* Primary contact */}
                    <div className="bg-slate-50 rounded-2xl p-5 mb-4">
                        <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                            {t("admin.detail.primaryContact")}
                        </p>
                        {primaryGuest && (
                            <p className="text-lg font-black text-slate-900 mb-0.5">
                                {primaryGuest.firstName} {primaryGuest.lastName}
                            </p>
                        )}
                        <p className="text-sm font-bold text-slate-500">{r.email}</p>
                        {r.phone && <p className="text-sm font-bold text-slate-500">{r.phone}</p>}
                    </div>

                    {/* Stay details */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-slate-50 rounded-2xl p-4">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">
                                {t("admin.detail.arrival")}
                            </p>
                            <p className="font-black text-slate-900">{formatDate(r.arrivalDate, locale)}</p>
                            {r.arrivalTime && <p className="text-sm font-bold text-slate-400">{r.arrivalTime}</p>}
                        </div>
                        <div className="bg-slate-50 rounded-2xl p-4">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">
                                {t("admin.detail.departure")}
                            </p>
                            <p className="font-black text-slate-900">{formatDate(r.departureDate, locale)}</p>
                            <p className="text-sm font-bold text-slate-400">
                                {t("admin.detail.nights", { count: r.nightCount })}
                            </p>
                        </div>
                    </div>

                    {/* All guests */}
                    {r.guests.length > 0 && (
                        <div className="mb-4">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                                {t("admin.detail.registeredGuests")}
                            </p>
                            <div className="space-y-2">
                                {r.guests.map((g, i) => (
                                    <div key={i} className="bg-slate-50 rounded-xl px-4 py-3 space-y-1">
                                        <div className="flex items-center justify-between gap-4">
                                            <span className="font-black text-slate-900 text-sm whitespace-nowrap">
                                                {g.firstName} {g.lastName}
                                            </span>
                                            <div className="flex items-center gap-3 text-xs font-bold text-slate-400 flex-wrap justify-end">
                                                <span>{g.dateOfBirth}</span>
                                                <span className="uppercase">{g.nationality}</span>
                                                {g.documentNumber && <span>{g.documentNumber}</span>}
                                            </div>
                                        </div>
                                        {(g.street ?? g.city ?? g.zip ?? g.country) && (
                                            <p className="text-xs font-bold text-slate-400">
                                                {[g.street, g.zip, g.city, g.country].filter(Boolean).join(", ")}
                                            </p>
                                        )}
                                        {g.purposeOfStay && (
                                            <p className="text-xs font-bold text-slate-400">
                                                {t("admin.detail.purposeOfStay")}: {g.purposeOfStay}
                                            </p>
                                        )}
                                        {g.bvcStatus === "preregistered" && (
                                            <div className="flex items-center gap-1.5 text-xs font-black text-emerald-600">
                                                <CheckCircle2 className="size-3.5 shrink-0" />
                                                <span>
                                                    {t("admin.detail.bvc.guestPreregistered")}
                                                    {g.bvcCardTemplate ? ` — ${g.bvcCardTemplate}` : ""}
                                                    {g.bvcSentAt ? ` · ${new Date(g.bvcSentAt).toLocaleDateString(locale)}` : ""}
                                                </span>
                                            </div>
                                        )}
                                        {g.bvcStatus === "error" && (
                                            <div className="flex items-center gap-1.5 text-xs font-black text-red-500">
                                                <AlertCircle className="size-3.5 shrink-0" />
                                                <span>
                                                    {t("admin.detail.bvc.guestError")}
                                                    {g.bvcError ? `: ${g.bvcError}` : ""}
                                                </span>
                                            </div>
                                        )}

                                        {/* Ubyport-fields correction — only for foreign guests
                                            (CZ is never reported). Lets the admin fix a rejected
                                            record (e.g. bad nationality → error 105) and resend. */}
                                        {g.nationality.trim().toUpperCase() !== "CZ" && (
                                            editingGuest === i ? (
                                                <GuestUbyportEdit
                                                    reservationId={r.id}
                                                    guestIndex={i}
                                                    initialNationality={g.nationality}
                                                    initialDocumentNumber={g.documentNumber ?? ""}
                                                    initialPurposeOfStay={g.purposeOfStay ?? ""}
                                                    queryKey={queryKey}
                                                    onDone={() => setEditingGuest(null)}
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingGuest(i)}
                                                    className="mt-1 inline-flex items-center gap-1 text-xs font-black text-brand hover:underline"
                                                >
                                                    <IdCard className="size-3.5 shrink-0" />
                                                    {t("admin.detail.ubyportEdit.trigger")}
                                                </button>
                                            )
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Payment */}
                    <div className={cn(
                        "rounded-2xl p-5 mb-4 border",
                        r.paymentStatus === "paid"
                            ? "bg-green-50 border-green-200"
                            : "bg-amber-50 border-amber-200",
                    )}>
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-0.5">
                                    {t("admin.detail.totalAmount")}
                                </p>
                                <p className="text-2xl font-black text-slate-900">
                                    {formatCurrency(r.totalAmount, currency, locale)}
                                </p>
                            </div>
                            <span className={cn(
                                "px-3 py-1 rounded-xl font-black text-sm border",
                                r.paymentStatus === "paid"
                                    ? "bg-green-100 text-green-700 border-green-300"
                                    : "bg-amber-100 text-amber-700 border-amber-300",
                            )}>
                                {t(`common.paymentStatus.${r.paymentStatus}`, { defaultValue: r.paymentStatus })}
                            </span>
                        </div>

                        {r.priceBreakdown && Object.keys(r.priceBreakdown).length > 0 && (
                            <div className="space-y-1 text-sm font-bold text-slate-600 mb-3 border-t border-slate-200 pt-3">
                                {(r.priceBreakdown["spot"] ?? 0) > 0 && (
                                    <div className="flex justify-between">
                                        <span>{t("admin.detail.spotPrice")}</span>
                                        <span>{formatCurrency(r.priceBreakdown["spot"] ?? 0, currency, locale)}</span>
                                    </div>
                                )}
                                {(r.priceBreakdown["taxes"] ?? 0) > 0 && (
                                    <div className="flex justify-between">
                                        <span>{t("admin.detail.localTax")}</span>
                                        <span>{formatCurrency(r.priceBreakdown["taxes"] ?? 0, currency, locale)}</span>
                                    </div>
                                )}
                                {(r.priceBreakdown["pet"] ?? 0) > 0 && (
                                    <div className="flex justify-between">
                                        <span>{t("admin.detail.petFee")}</span>
                                        <span>{formatCurrency(r.priceBreakdown["pet"] ?? 0, currency, locale)}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {isPending && (
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => markPaidMutation.mutate({ id: r.id, method: "cash" })}
                                    disabled={markPaidMutation.isPending}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-charcoal text-white font-black text-sm hover:bg-slate-700 transition-colors active:scale-95 disabled:opacity-50"
                                >
                                    <Banknote className="size-4" />
                                    {t("admin.detail.markPaidCash")}
                                </button>
                                <button
                                    onClick={() => markPaidMutation.mutate({ id: r.id, method: "bank_transfer" })}
                                    disabled={markPaidMutation.isPending}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand text-white font-black text-sm hover:bg-brand-dark transition-colors active:scale-95 disabled:opacity-50"
                                >
                                    <CreditCard className="size-4" />
                                    {t("admin.detail.markPaidTransfer")}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Reactivate cancelled reservation */}
                    {r.status === "cancelled" && (
                        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-4">
                            <p className="text-xs font-black uppercase tracking-widest text-blue-700 mb-1">
                                {t("admin.detail.reactivateTitle")}
                            </p>
                            <p className="text-sm font-bold text-slate-600 mb-3">
                                {t("admin.detail.reactivateHint")}
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => {
                                        if (window.confirm(t("admin.detail.reactivateConfirm"))) {
                                            reactivateMutation.mutate({ id: r.id, method: "cash" })
                                        }
                                    }}
                                    disabled={reactivateMutation.isPending}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-charcoal text-white font-black text-sm hover:bg-slate-700 transition-colors active:scale-95 disabled:opacity-50"
                                >
                                    <RotateCcw className="size-4" />
                                    {t("admin.detail.reactivateCash")}
                                </button>
                                <button
                                    onClick={() => {
                                        if (window.confirm(t("admin.detail.reactivateConfirm"))) {
                                            reactivateMutation.mutate({ id: r.id, method: "bank_transfer" })
                                        }
                                    }}
                                    disabled={reactivateMutation.isPending}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand text-white font-black text-sm hover:bg-brand-dark transition-colors active:scale-95 disabled:opacity-50"
                                >
                                    <RotateCcw className="size-4" />
                                    {t("admin.detail.reactivateTransfer")}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Notes */}
                    {r.notes && (
                        <div className="bg-slate-50 rounded-2xl p-4 mb-4">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">
                                {t("admin.detail.notes")}
                            </p>
                            <p className="text-sm font-bold text-slate-700">{r.notes}</p>
                        </div>
                    )}

                    {/* Referral source */}
                    {r.referralSource && (
                        <div className="bg-slate-50 rounded-2xl p-4 mb-4">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">
                                {t("admin.detail.referralSource")}
                            </p>
                            <p className="text-sm font-bold text-slate-700">{r.referralSource}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-2 flex-wrap">
                            {r.status !== "cancelled" && r.status !== "completed" && (
                                <button
                                    onClick={() => {
                                        if (window.confirm(t("admin.detail.confirmCancel"))) {
                                            cancelMutation.mutate(r.id)
                                        }
                                    }}
                                    disabled={cancelMutation.isPending}
                                    className="px-4 py-2 rounded-xl bg-red-50 text-red-500 font-bold text-sm hover:bg-red-100 transition-colors disabled:opacity-50"
                                >
                                    {t("admin.detail.cancelReservation")}
                                </button>
                            )}
                            <a
                                href={`/api/reservations/${r.id}/receipt?lang=${i18n.language}`}
                                download
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-colors"
                            >
                                <Download className="size-4" />
                                {t("admin.detail.downloadInvoice")}
                            </a>
                            {bvcAvailable && (
                                <button
                                    onClick={() => {
                                        if (bvcSent && !window.confirm(t("admin.detail.bvc.confirmResend"))) return
                                        sendBvcMutation.mutate(r.id)
                                    }}
                                    disabled={sendBvcMutation.isPending}
                                    className={cn(
                                        "flex items-center gap-1.5 px-4 py-2 rounded-xl font-bold text-sm transition-colors disabled:opacity-50",
                                        bvcSent
                                            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                            : "bg-blue-50 text-blue-700 hover:bg-blue-100",
                                    )}
                                    title={bvcSent ? t("admin.detail.bvc.alreadySentTitle") : undefined}
                                >
                                    {bvcSent
                                        ? <CheckCircle2 className="size-4" />
                                        : <IdCard className="size-4" />}
                                    {sendBvcMutation.isPending
                                        ? t("admin.detail.bvc.sending")
                                        : bvcSent
                                            ? t("admin.detail.bvc.resend")
                                            : t("admin.detail.bvc.send")}
                                </button>
                            )}
                        </div>
                        <Dialog.Close asChild>
                            <button className="px-6 py-2.5 rounded-[28px] bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-colors">
                                {t("admin.detail.close")}
                            </button>
                        </Dialog.Close>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    )
}
